import useSWR, { preload } from 'swr';
import {
  listDealsApi,
  createDealApi,
  updateDealApi,
  deleteDealApi,
  getDealApi,
} from 'src/api/deal';
import type {
  Deal,
  DealVersion,
  ListDealsRequestContent,
  CreateDealRequestContent,
  CreateDealResponseContent,
  UpdateDealRequestContent,
  GetDealResponseContent,
  DeleteDealRequestContent,
} from '@equaliq/api-model-v2';
import type { DealWithTitle, DealParticipantSummary } from 'src/types/deal.types';

// Hook-internal: cast type for DealVersion.content (Smithy Document → unknown)
interface DealVersionContent {
  title?: string;
  description?: string;
  changeReason?: string;
}

const getFallbackTitle = (dealId: string) => `Deal ${dealId.slice(0, 8)}`;

// Stable identity for the "no data yet" case. `data ?? []` minted a fresh
// array every render, so every downstream useMemo keyed on `deals`
// (useDealPipelineData, usePeopleData, useVaultData) recomputed each render.
// In DealsTableView that unstable `data` reference fed useReactTable's
// controlled state and drove an infinite update-depth loop (React #185).
const EMPTY_DEALS: DealWithTitle[] = [];

const getDealVersionContent = (content: unknown): DealVersionContent | undefined => {
  if (!content || typeof content !== 'object') {
    return undefined;
  }

  const maybeContent = content as DealVersionContent;
  return maybeContent;
};

const fetcher = async (orgId?: string) => {
  const request: ListDealsRequestContent = {};
  if (orgId) {
    request.orgId = orgId;
  }

  const response = await listDealsApi(request);
  const dealArray = Object.values(response.deals ?? {}) as Deal[];

  const enrichedDeals = await Promise.all(
    dealArray.map(async (deal): Promise<DealWithTitle> => {
      try {
        const detail = await getDealApi({ dealId: deal.dealId, includeVersions: true });
        const versions = Object.values(detail.versions ?? {}) as DealVersion[];
        const latest = [...versions].sort((a, b) => b.versionNumber - a.versionNumber)[0];
        const content = getDealVersionContent(latest?.content);

        // /deals/get returns `files`, `latestAnalysis`, and Phase 3
        // (`threads`, `participants`, deal.sourceType) as untyped passthroughs
        // — see api-server/legacy-handlers/deals.ts. Cast at the boundary
        // so the kanban can show real document counts, analysis state, and
        // the source-channel badge.
        const detailExtras = detail as unknown as {
          deal?: { sourceType?: 'upload' | 'email' | 'imessage' | 'manual' };
          files?: Array<{ fileName?: string }>;
          participants?: DealParticipantSummary[];
          latestAnalysis?: {
            status?: string;
            primaryContractId?: string;
            dealValue?: { amount?: number; currency?: string };
            linkedThreadCount?: number;
            participantCount?: number;
          };
        };
        const files = detailExtras.files ?? [];
        const latestAnalysis = detailExtras.latestAnalysis;

        return {
          ...deal,
          title: content?.title || getFallbackTitle(deal.dealId),
          description: content?.description,
          latestStage: latest?.stage,
          documentsCount: files.length,
          firstFileName: files[0]?.fileName,
          latestAnalysisStatus: latestAnalysis?.status,
          primaryContractId: latestAnalysis?.primaryContractId,
          dealValueAmount: latestAnalysis?.dealValue?.amount,
          dealValueCurrency: latestAnalysis?.dealValue?.currency,
          sourceType: detailExtras.deal?.sourceType,
          linkedThreadCount: latestAnalysis?.linkedThreadCount,
          participantCount: latestAnalysis?.participantCount,
          participants: detailExtras.participants,
        };
      } catch {
        return {
          ...deal,
          title: getFallbackTitle(deal.dealId),
        };
      }
    })
  );

  return enrichedDeals;
};

export const preloadDeals = (orgId: string) =>
  preload(['deals', orgId], () => fetcher(orgId)).catch(() => undefined);

export const useDealOperations = (orgId?: string) => {
  const swrKey = orgId ? ['deals', orgId] : null;

  const { data, error, isLoading, mutate } = useSWR(swrKey, () => fetcher(orgId));

  const deals = data ?? EMPTY_DEALS;

  const refreshDeals = async () => {
    await mutate();
  };

  const createDeal = async (
    request: CreateDealRequestContent
  ): Promise<CreateDealResponseContent> => {
    const result = await createDealApi(request);
    await mutate();
    return result;
  };

  const updateDeal = async (request: UpdateDealRequestContent) => {
    await updateDealApi(request);
    await mutate();
  };

  const deleteDeal = async (dealId: string) => {
    const request: DeleteDealRequestContent = { dealId };
    await mutate(
      deals.filter((deal) => deal.dealId !== dealId),
      false
    );

    try {
      await deleteDealApi(request);
      await mutate();
    } catch (errorDuringDelete) {
      await mutate();
      throw errorDuringDelete;
    }
  };

  const getDeal = async (dealId: string): Promise<GetDealResponseContent> => {
    return getDealApi({
      dealId,
      includeVersions: true,
      includeDeliverables: true,
    });
  };

  return {
    deals,
    isLoading,
    error,
    refreshDeals,
    createDeal,
    updateDeal,
    deleteDeal,
    getDeal,
  };
};
